import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { PhotoAttach, type LocalPhoto } from '../components/PhotoAttach';
import { AddressInput } from '../components/AddressInput';
import { useUserOrg } from '../lib/org';
import {
  getSite,
  createForm,
  setFormSignaturePath,
  findOrCreateSubcontractor,
  createSubcontractorInduction,
  type Site,
} from '../lib/db';
import { uploadSignature } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { enqueue as enqueueOutbox, blobToDataUrl } from '../lib/outbox';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  SUBBIE_INSURANCE, SUBBIE_COMPETENCE, INDUCTION_TOPICS, INDUCTION_DECLARATION, CARD_TYPES,
} from '../lib/subbieInduction';

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

const INSURANCE = SUBBIE_INSURANCE;
const COMPETENCE = SUBBIE_COMPETENCE;

export function FormSubbie() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const formIdRef = useRef<string>(crypto.randomUUID());
  const sigRef = useRef<SignaturePadHandle>(null);
  const inductorSigRef = useRef<SignaturePadHandle>(null);

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [personName, setPersonName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [trade, setTrade] = useState('');
  const [contact, setContact] = useState('');
  const [mobile, setMobile] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [address, setAddress] = useState('');
  const [plExp, setPlExp] = useState('');
  const [elExp, setElExp] = useState('');
  const [insurance, setInsurance] = useState<boolean[]>([false, false, false]);
  const [isFirstAider, setIsFirstAider] = useState(false);
  const [faExpiry, setFaExpiry] = useState('');
  const [competence, setCompetence] = useState<boolean[]>([false, false, false, false]);
  const [approver, setApprover] = useState('');
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [cardType, setCardType] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [topics, setTopics] = useState<boolean[]>(INDUCTION_TOPICS.map(() => false));
  const [understood, setUnderstood] = useState(false);
  const [needsSupport, setNeedsSupport] = useState(false);
  const [supportNotes, setSupportNotes] = useState('');

  useEffect(() => {
    if (!siteId) return;
    getSite(siteId)
      .then((s) => setSite(s))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !approver) setApprover(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site) return;
    setError(null);
    if (!personName.trim() || !companyName.trim() || !trade.trim()) {
      setError('Person name, company and trade are required.');
      return;
    }
    if (!topics.some(Boolean)) {
      setError('Tick the induction topics you covered with them.');
      return;
    }
    if (!understood) {
      setError('The subcontractor needs to confirm they understood the induction.');
      return;
    }
    if (sigRef.current?.isEmpty()) {
      setError('The subcontractor needs to sign.');
      return;
    }
    if (!approver.trim()) {
      setError('Add the name of the person who gave the induction.');
      return;
    }
    setSubmitting(true);
    try {
      const formId = formIdRef.current;
      const sigBlob = await sigRef.current!.toBlob();

      // Inductor signature is small — stored inline as a data URL so the
      // same payload works online and offline.
      const inductorSig = inductorSigRef.current && !inductorSigRef.current.isEmpty()
        ? await blobToDataUrl(await inductorSigRef.current.toBlob())
        : null;
      const formDataCore = {
        person_name: personName.trim(),
        company_name: companyName.trim(),
        trade: trade.trim(),
        contact: contact.trim(),
        mobile: mobile.trim(),
        emergency_contact_name: emergencyName.trim(),
        emergency_contact_phone: emergencyPhone.trim(),
        address: address.trim(),
        pl_expires: plExp,
        el_expires: elExp,
        insurance,
        competence,
        card_type: cardType,
        card_number: cardNumber.trim(),
        card_expiry: cardExpiry || null,
        first_aider: isFirstAider,
        first_aid_expiry: isFirstAider ? (faExpiry || null) : null,
        induction_topics: topics,
        understood,
        needs_support: needsSupport,
        support_notes: needsSupport ? supportNotes.trim() : '',
        inductor_name: approver.trim(),
        inductor_signature_data_url: inductorSig,
      };

      // Offline path — queue the whole induction (form row + signature + photos
      // + subcontractor cascade) for replay when signal returns.
      if (!isOnline()) {
        const sigDataUrl = await blobToDataUrl(sigBlob);
        const photoDataUrls: string[] = [];
        for (const p of photos) photoDataUrls.push(await blobToDataUrl(p.blob));
        await enqueueOutbox('form.subbie_induction', org.org_id, {
          formId,
          orgId: org.org_id,
          siteId: site.id,
          kind: 'subcontractor_induction',
          title: `Induction: ${personName.trim()} (${companyName.trim()})`,
          performed_by_name: approver.trim(),
          data: formDataCore,
          signature_data_url: sigDataUrl,
          photo_data_urls: photoDataUrls,
          photo_bucket: 'subbie-induction-photos',
          subbie_cascade: {
            company_name: companyName.trim(),
            trade: trade.trim() || null,
            main_contact: contact.trim() || null,
            mobile: mobile.trim() || null,
            address: address.trim() || null,
            pl_insurance_expiry: plExp || null,
            el_insurance_expiry: elExp || null,
            approved_by: approver.trim() || null,
            first_aider: isFirstAider,
            first_aid_cert_expiry: faExpiry || null,
            person_name: personName.trim(),
          },
        });
        nav(`/sites/${site.id}/forms`);
        return;
      }

      // Online path — current behaviour.
      // Upload photos
      const photoPaths: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const path = `${org.org_id}/form-${formId}/${i}.jpg`;
        const { error: upErr } = await supabase.storage
          .from('subbie-induction-photos')
          .upload(path, photos[i].blob, { contentType: 'image/jpeg', upsert: true });
        if (upErr) throw upErr;
        photoPaths.push(path);
      }

      // Create or find the subcontractor row first (so the induction can link)
      const sub = await findOrCreateSubcontractor({
        orgId: org.org_id,
        company_name: companyName.trim(),
        trade: trade.trim() || null,
        main_contact: contact.trim() || null,
        mobile: mobile.trim() || null,
        address: address.trim() || null,
        pl_insurance_expiry: plExp || null,
        el_insurance_expiry: elExp || null,
        approved_by: approver.trim() || null,
        first_aider: isFirstAider,
        first_aid_cert_expiry: faExpiry || null,
      });

      await createForm({
        id: formId,
        orgId: org.org_id,
        siteId: site.id,
        kind: 'subcontractor_induction',
        title: `Induction: ${personName.trim()} (${companyName.trim()})`,
        performed_by_name: approver.trim(),
        data: {
          ...formDataCore,
          photo_paths: photoPaths,
          subcontractor_id: sub.id,
        },
      });

      const sigPath = await uploadSignature({
        orgId: org.org_id,
        signInId: `form-${formId}`,
        side: 'in',
        blob: sigBlob,
      });
      await setFormSignaturePath(formId, sigPath);

      // Link an induction row to the subbie
      await createSubcontractorInduction({
        org_id: org.org_id,
        subcontractor_id: sub.id,
        site_id: site.id,
        person_name: personName.trim(),
        form_id: formId,
      });

      nav(`/sites/${site.id}/forms`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) return <AppShell title="Subbie induction" back={`/sites/${siteId ?? ''}/forms`}><LoadingIndicator /></AppShell>;
  if (!site || !org) return <AppShell title="Subbie induction" back={`/sites/${siteId ?? ''}/forms`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;

  return (
    <AppShell title="Subbie induction" back={`/sites/${site.id}/forms`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}>
          Record who you inducted, check their insurance, card and RAMS, and tick off each
          induction topic as you go through it. Both of you sign at the end.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <h3 className="section-title">Person being inducted</h3>
        <div className="field"><label htmlFor="sub-person-name">Person name *</label><input id="sub-person-name" type="text" required placeholder="e.g. Pete Hill" value={personName} onChange={(e) => setPersonName(e.target.value)} /></div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Emergency contact</h3>
        <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
          Who to call if there's an accident — partner, parent, brother, anyone close. Optional but strongly recommended.
        </p>
        <div className="field-row">
          <div className="field"><label htmlFor="sub-emergency-name">Name</label><input id="sub-emergency-name" type="text" placeholder="e.g. Sarah Doyle (wife)" value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} /></div>
          <div className="field"><label htmlFor="sub-emergency-phone">Phone</label><input id="sub-emergency-phone" type="tel" value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} /></div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Company</h3>
        <div className="field"><label htmlFor="sub-company-name">Company name *</label><input id="sub-company-name" type="text" required placeholder="e.g. ACME Roofing Ltd" value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></div>
        <div className="field"><label htmlFor="sub-trade">Trade *</label><input id="sub-trade" type="text" required placeholder="e.g. Bricklayer, Scaffolder, Joiner" value={trade} onChange={(e) => setTrade(e.target.value)} /></div>
        <div className="field-row">
          <div className="field"><label htmlFor="sub-main-contact">Main contact (if not the person above)</label><input id="sub-main-contact" type="text" placeholder="e.g. Foreman / Director" value={contact} onChange={(e) => setContact(e.target.value)} /></div>
          <div className="field"><label htmlFor="sub-main-mobile">Mobile</label><input id="sub-main-mobile" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} /></div>
        </div>
        <AddressInput
          label="Address"
          value={address}
          onChange={setAddress}
        />

        <h3 className="section-title" style={{ marginTop: 22 }}>Insurance</h3>
        <div className="check-list">
          {INSURANCE.map((label, i) => (
            <label key={i} className="check-row">
              <input type="checkbox" checked={insurance[i]} onChange={(e) => {
                const next = insurance.slice();
                next[i] = e.target.checked;
                setInsurance(next);
              }} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="field-row" style={{ marginTop: 12 }}>
          <div className="field"><label htmlFor="sub-pl-exp">Public Liability expires</label><input id="sub-pl-exp" type="date" value={plExp} onChange={(e) => setPlExp(e.target.value)} /></div>
          <div className="field"><label htmlFor="sub-el-exp">Employers Liability expires</label><input id="sub-el-exp" type="date" value={elExp} onChange={(e) => setElExp(e.target.value)} /></div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>First aid</h3>
        <div className="field">
          <label className="check-row" style={{ alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={isFirstAider}
              onChange={(e) => setIsFirstAider(e.target.checked)}
            />
            <span>
              <strong>Qualified first aider</strong>
              <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                Tick if they hold a current First Aid at Work, Emergency First Aid at Work, or equivalent certificate.
              </span>
            </span>
          </label>
        </div>
        {isFirstAider && (
          <div className="field">
            <label htmlFor="fa-expiry">First aid certificate expires</label>
            <input
              id="fa-expiry"
              type="date"
              value={faExpiry}
              onChange={(e) => setFaExpiry(e.target.value)}
            />
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
              FAW certs run 3 years from issue. Leave blank if you don&apos;t have the cert in front of you.
            </p>
          </div>
        )}

        <h3 className="section-title" style={{ marginTop: 22 }}>Skills card</h3>
        <div className="field">
          <label htmlFor="sub-card-type">Card type</label>
          <select id="sub-card-type" value={cardType} onChange={(e) => setCardType(e.target.value)}>
            {CARD_TYPES.map((c) => <option key={c} value={c}>{c || 'Choose…'}</option>)}
          </select>
        </div>
        {cardType && cardType !== 'None held' && (
          <div className="field-row">
            <div className="field"><label htmlFor="sub-card-no">Card number</label><input id="sub-card-no" type="text" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} /></div>
            <div className="field"><label htmlFor="sub-card-exp">Expires</label><input id="sub-card-exp" type="date" value={cardExpiry} onChange={(e) => setCardExpiry(e.target.value)} /></div>
          </div>
        )}

        <h3 className="section-title" style={{ marginTop: 22 }}>Photos of insurance / cards</h3>
        <PhotoAttach photos={photos} onChange={setPhotos} />

        <h3 className="section-title" style={{ marginTop: 22 }}>Competence</h3>
        <div className="check-list">
          {COMPETENCE.map((label, i) => (
            <label key={i} className="check-row">
              <input type="checkbox" checked={competence[i]} onChange={(e) => {
                const next = competence.slice();
                next[i] = e.target.checked;
                setCompetence(next);
              }} />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Induction — what you covered *</h3>
        <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
          Talk them through each point, then tick it.{' '}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }}
            onClick={() => setTopics(INDUCTION_TOPICS.map(() => true))}>Tick all</button>
        </p>
        <div className="check-list">
          {INDUCTION_TOPICS.map((label, i) => (
            <label key={i} className="check-row">
              <input type="checkbox" checked={topics[i]} onChange={(e) => {
                const next = topics.slice();
                next[i] = e.target.checked;
                setTopics(next);
              }} />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Declaration *</h3>
        <div className="card">
          <p style={{ marginTop: 0 }}>By signing, the subcontractor confirms:</p>
          <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
            {INDUCTION_DECLARATION.map((d) => <li key={d}>{d}</li>)}
          </ul>
          <label className="check-row">
            <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
            <span><strong>They have understood the induction</strong></span>
          </label>
          <label className="check-row">
            <input type="checkbox" checked={needsSupport} onChange={(e) => setNeedsSupport(e.target.checked)} />
            <span>They needed an interpreter or extra help to understand it</span>
          </label>
          {needsSupport && (
            <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
              <label htmlFor="sub-support">How was this handled?</label>
              <input id="sub-support" type="text" placeholder="e.g. Induction given in Polish by foreman Tomasz" value={supportNotes} onChange={(e) => setSupportNotes(e.target.value)} />
            </div>
          )}
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>RAMS hand-over</h3>
        <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)', borderColor: 'var(--brand-light)' }}>
          <p style={{ margin: '0 0 8px' }}>
            <strong>Subbie sends their RAMS by email — we upload it.</strong>
          </p>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Tap below to open a pre-written email on the subbie's phone. They attach their RAMS PDF and hit send. We'll receive it and upload to the RAMS library for this site.
          </p>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              const subject = encodeURIComponent(`RAMS for ${site?.name ?? 'site'} — ${companyName.trim() || 'your company'}`);
              const body = encodeURIComponent(
                `Hi,\n\nPlease attach your RAMS PDF for the work we'll be doing on ${site?.name ?? 'site'}.\n\n` +
                `Company: ${companyName.trim() || '(your company)'}\n` +
                `Trade: ${trade.trim() || '(trade)'}\n` +
                `Person being inducted: ${personName.trim() || '(name)'}\n\n` +
                `Once received, your RAMS will be reviewed and stored on the site file. Please ensure it's site-specific where required.\n\n` +
                `Thanks.`
              );
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }}
          >
            ✉ Open email to send RAMS
          </button>
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 11 }}>
            Or the main contractor can upload directly via the RAMS library on the site page.
          </p>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Subcontractor signature *</h3>
        <SignaturePad ref={sigRef} label="Subcontractor signs" />

        <h3 className="section-title" style={{ marginTop: 22 }}>Induction given by *</h3>
        <div className="field">
          <label htmlFor="sub-approver">Name</label>
          <input id="sub-approver" type="text" value={approver} onChange={(e) => setApprover(e.target.value)} />
        </div>
        <SignaturePad ref={inductorSigRef} label="Inductor signs" />
        <p className="muted" style={{ margin: '8px 0 12px', fontSize: 12 }}>
          This record contains personal information. It's stored securely and only visible to people in your company.
        </p>

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save induction'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}/forms`)}>Cancel</button>
        </div>
      </form>
    </AppShell>
  );
}
